import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import useProjects from '../hooks/useProjects';
import API_BASE from '../utils/api.js';

const ALL_PROJECTS_VALUE = '__all_projects__';

function formatCurrency(v) {
  const n = Number(v);
  if (!n || isNaN(n)) return '$0.00';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [todos, setTodos] = useState([]);
  const [team, setTeam] = useState([]);
  const [invoiceTotals, setInvoiceTotals] = useState({ paid: 0, unpaid: 0 });
  const [poTotals, setPoTotals] = useState({ paid: 0, open: 0 });
  const [invoiceCounts, setInvoiceCounts] = useState({ unpaid: 0, total: 0 });
  const [poCounts, setPoCounts] = useState({ outstanding: 0, total: 0 });
  const [invoiceStatsByProject, setInvoiceStatsByProject] = useState({});
  const [poStatsByProject, setPoStatsByProject] = useState({});
  const [invoiceProjectId, setInvoiceProjectId] = useState('');
  const [poProjectId, setPoProjectId] = useState('');
  const [agendaSort, setAgendaSort] = useState('assignee-asc');
  const [agendaFilter, setAgendaFilter] = useState('all');

  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const { projects: recentProjects } = useProjects({ activeOnly: true, isOpen: true });

  /* ------------------ LOAD ALL DATA ------------------ */
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        setLoading(true);

        const [projectsRes, todosRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${userId}`),
          fetch(`${API_BASE}/api/todos/${userId}`),
        ]);

        const [projectList, todosData] = await Promise.all([
          projectsRes.ok ? projectsRes.json() : [],
          todosRes.ok ? todosRes.json() : [],
        ]);

        setProjects(projectList || []);
        const normalizedTodos = (todosData || []).map((t) => ({
          ...t,
          assignees: Array.isArray(t.assignees)
            ? t.assignees.map((a) => (typeof a === 'string' ? a : a.email)).filter(Boolean)
            : [],
        }));
        setTodos(normalizedTodos);

        const projectPromises = (projectList || []).map(async (p) => {
          const pid = p?.projectId || p?.id;
          if (!pid) {
            return {
              invoices: [],
              purchaseOrders: [],
            };
          }
          const [invRes, poRes] = await Promise.allSettled([
            fetch(`${API_BASE}/api/invoices/${userId}/${pid}`),
            fetch(`${API_BASE}/api/purchase-orders/${userId}/${pid}`),
          ]);

          const invData =
            invRes.status === 'fulfilled' && invRes.value.ok ? await invRes.value.json() : [];
          const poData =
            poRes.status === 'fulfilled' && poRes.value.ok ? await poRes.value.json() : [];

          return {
            invoices: Array.isArray(invData) ? invData : [],
            purchaseOrders: Array.isArray(poData) ? poData : [],
          };
        });

        const allResults = await Promise.all(projectPromises);

        let paidInvoices = 0,
          unpaidInvoices = 0,
          paidPOs = 0,
          openPOs = 0,
          unpaidInvoiceCount = 0,
          totalInvoiceCount = 0,
          outstandingPoCount = 0,
          totalPoCount = 0;

        const invoiceStats = {};
        const poStats = {};

        allResults.forEach((result, index) => {
          const project = projectList?.[index];
          const projectId = project?.projectId || project?.id;
          if (!projectId) return;

          const invoices = result?.invoices || [];
          const purchaseOrders = result?.purchaseOrders || [];

          let projPaidInvoices = 0;
          let projUnpaidInvoices = 0;
          let projUnpaidInvoiceCount = 0;
          let projTotalInvoiceCount = 0;
          let projPaidPOs = 0;
          let projOpenPOs = 0;
          let projOutstandingPoCount = 0;
          let projTotalPoCount = 0;

          invoices.forEach((inv) => {
            const total =
              Number(inv?.totals?.grandTotal) ||
              Number(inv?.totals?.total) ||
              Number(inv?.total) ||
              0;
            const paidFallback = Number(inv?.totals?.paid || 0);
            const remaining = Math.max(0, Number(inv?.remainingBalance ?? total - paidFallback));
            const paidAmount = Math.max(total - remaining, 0);
            totalInvoiceCount += 1;
            projTotalInvoiceCount += 1;
            paidInvoices += paidAmount;
            projPaidInvoices += paidAmount;
            unpaidInvoices += remaining;
            projUnpaidInvoices += remaining;
            if (remaining > 0) {
              unpaidInvoiceCount += 1;
              projUnpaidInvoiceCount += 1;
            }
          });

          purchaseOrders.forEach((po) => {
            const total = Number(po?.total || 0);
            const payments = Number(po?.payments || 0);
            totalPoCount += 1;
            projTotalPoCount += 1;
            paidPOs += payments;
            projPaidPOs += payments;
            const outstanding = Math.max(0, total - payments);
            openPOs += outstanding;
            if (outstanding > 0) outstandingPoCount += 1;
            if (outstanding > 0) projOutstandingPoCount += 1;
            projOpenPOs += outstanding;
          });

          invoiceStats[projectId] = {
            paid: projPaidInvoices,
            unpaid: projUnpaidInvoices,
            totalCount: projTotalInvoiceCount,
            unpaidCount: projUnpaidInvoiceCount,
          };
          poStats[projectId] = {
            paid: projPaidPOs,
            open: projOpenPOs,
            totalCount: projTotalPoCount,
            outstandingCount: projOutstandingPoCount,
          };
        });

        setInvoiceTotals({
          paid: isNaN(paidInvoices) ? 0 : paidInvoices,
          unpaid: isNaN(unpaidInvoices) ? 0 : unpaidInvoices,
        });
        setPoTotals({
          paid: isNaN(paidPOs) ? 0 : paidPOs,
          open: isNaN(openPOs) ? 0 : openPOs,
        });
        setInvoiceCounts({
          unpaid: unpaidInvoiceCount,
          total: totalInvoiceCount,
        });
        setPoCounts({
          outstanding: outstandingPoCount,
          total: totalPoCount,
        });
        setInvoiceStatsByProject(invoiceStats);
        setPoStatsByProject(poStats);
      } catch (err) {
        console.error('Dashboard load error', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => setTeam(Array.isArray(data.team) ? data.team : []))
      .catch(() => setTeam([]));
  }, [userId]);

  /* ------------------ COMPUTED VALUES ------------------ */
  const totalInvoices = (invoiceTotals.paid || 0) + (invoiceTotals.unpaid || 0);
  const totalPOs = (poTotals.paid || 0) + (poTotals.open || 0);
  const openBalance = invoiceTotals.unpaid || 0;
  const projectIds = useMemo(
    () => (projects || []).map((p) => p?.projectId || p?.id).filter(Boolean),
    [projects]
  );

  const allInvoiceStats = useMemo(() => {
    return projectIds.reduce(
      (acc, projectId) => {
        const stats = invoiceStatsByProject?.[projectId];
        if (!stats) return acc;
        acc.paid += Number(stats.paid || 0);
        acc.unpaid += Number(stats.unpaid || 0);
        acc.totalCount += Number(stats.totalCount || 0);
        acc.unpaidCount += Number(stats.unpaidCount || 0);
        return acc;
      },
      { paid: 0, unpaid: 0, totalCount: 0, unpaidCount: 0 }
    );
  }, [projectIds, invoiceStatsByProject]);

  const allPoStats = useMemo(() => {
    return projectIds.reduce(
      (acc, projectId) => {
        const stats = poStatsByProject?.[projectId];
        if (!stats) return acc;
        acc.paid += Number(stats.paid || 0);
        acc.open += Number(stats.open || 0);
        acc.totalCount += Number(stats.totalCount || 0);
        acc.outstandingCount += Number(stats.outstandingCount || 0);
        return acc;
      },
      { paid: 0, open: 0, totalCount: 0, outstandingCount: 0 }
    );
  }, [projectIds, poStatsByProject]);

  const isAllInvoicesSelected = invoiceProjectId === ALL_PROJECTS_VALUE;
  const isAllPOsSelected = poProjectId === ALL_PROJECTS_VALUE;

  const selectedInvoiceStats = useMemo(() => {
    if (invoiceProjectId === ALL_PROJECTS_VALUE) return allInvoiceStats;
    if (!invoiceProjectId) return null;
    return invoiceStatsByProject?.[invoiceProjectId] || null;
  }, [invoiceProjectId, invoiceStatsByProject, allInvoiceStats]);

  const selectedPoStats = useMemo(() => {
    if (poProjectId === ALL_PROJECTS_VALUE) return allPoStats;
    if (!poProjectId) return null;
    return poStatsByProject?.[poProjectId] || null;
  }, [poProjectId, poStatsByProject, allPoStats]);

  const selectedInvoiceTotals = selectedInvoiceStats || { paid: 0, unpaid: 0 };
  const selectedPoTotals = selectedPoStats || { paid: 0, open: 0 };
  const selectedInvoiceCounts = selectedInvoiceStats
    ? { total: selectedInvoiceStats.totalCount, unpaid: selectedInvoiceStats.unpaidCount }
    : { total: 0, unpaid: 0 };
  const selectedPoCounts = selectedPoStats
    ? {
        total: selectedPoStats.totalCount,
        outstanding: selectedPoStats.outstandingCount,
      }
    : { total: 0, outstanding: 0 };

  const selectedOpenBalance = selectedInvoiceTotals.unpaid || 0;
  const selectedUnpaidInvoiceCount = selectedInvoiceCounts.unpaid || 0;
  const selectedOutstandingPoCount = selectedPoCounts.outstanding || 0;

  const today = new Date().toISOString().split('T')[0];
  const todayAgenda = useMemo(
    () => (todos || []).filter((t) => t.dueDate?.startsWith(today) && !t.completed),
    [todos, today]
  );

  const assigneeName = useCallback(
    (email) =>
      team.find((m) => m.email === email)?.name ||
      team.find((m) => m.email === email)?.email ||
      email,
    [team]
  );

  const agendaAssigneeKey = useCallback(
    (todo) => {
      const assignees = Array.isArray(todo?.assignees) ? todo.assignees : [];
      if (assignees.length === 0) return 'zzzzzzzz';
      const names = assignees
        .map((id) => assigneeName(id))
        .filter(Boolean)
        .map((name) => name.toLowerCase());
      if (names.length === 0) return assignees.join(', ').toLowerCase();
      return names.join(', ');
    },
    [assigneeName]
  );

  const sortedTodayAgenda = useMemo(() => {
    const filtered =
      agendaFilter === 'all'
        ? todayAgenda
        : todayAgenda.filter((item) => {
            const assignees = Array.isArray(item?.assignees) ? item.assignees : [];
            if (agendaFilter === 'unassigned') return assignees.length === 0;
            return assignees.includes(agendaFilter);
          });
    if (agendaSort === 'default') return filtered;
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aKey = agendaAssigneeKey(a);
      const bKey = agendaAssigneeKey(b);
      const compare = aKey.localeCompare(bKey);
      if (compare !== 0) return agendaSort === 'assignee-desc' ? -compare : compare;
      const aDue = a?.dueDate || '';
      const bDue = b?.dueDate || '';
      return aDue.localeCompare(bDue);
    });
    return sorted;
  }, [agendaFilter, agendaSort, agendaAssigneeKey, todayAgenda]);

  const agendaTeamOptions = useMemo(() => {
    const seen = new Map();
    (team || []).forEach((member) => {
      const email = member?.email;
      if (!email || seen.has(email)) return;
      const label = member?.name || member?.email || email;
      seen.set(email, label);
    });
    return Array.from(seen.entries())
      .map(([email, label]) => ({ email, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [team]);

  const selectedTotalInvoices =
    (selectedInvoiceTotals.paid || 0) + (selectedInvoiceTotals.unpaid || 0);
  const selectedTotalPOs = (selectedPoTotals.paid || 0) + (selectedPoTotals.open || 0);

  const invoiceChartData =
    selectedTotalInvoices > 0
      ? [
          { name: 'Paid', value: selectedInvoiceTotals.paid || 0 },
          { name: 'Unpaid', value: selectedInvoiceTotals.unpaid || 0 },
        ]
      : [{ name: 'No invoices', value: 1 }];

  const poChartData =
    selectedTotalPOs > 0
      ? [
          { name: 'Paid', value: selectedPoTotals.paid || 0 },
          { name: 'Open Balance', value: selectedPoTotals.open || 0 },
        ]
      : [{ name: 'No POs', value: 1 }];

  const CHART_COLORS = {
    Paid: '#dfe5d3',
    Unpaid: '#efe6dd',
    Overdue: '#d7dde0',
    'Open Balance': '#efe6dd',
    'No invoices': '#e2e5e7',
    'No POs': '#e2e5e7',
  };

  const recentProjectCards = useMemo(() => (recentProjects || []).slice(0, 8), [recentProjects]);
  const primaryProjectId = useMemo(() => {
    if (recentProjectCards.length > 0) return recentProjectCards[0].id;
    const fallback = projects?.[0];
    return fallback?.projectId || fallback?.id || null;
  }, [recentProjectCards, projects]);

  useEffect(() => {
    if (!invoiceProjectId) {
      if (projectIds.length > 0) setInvoiceProjectId(ALL_PROJECTS_VALUE);
      else if (primaryProjectId) setInvoiceProjectId(primaryProjectId);
    }
    if (!poProjectId) {
      if (projectIds.length > 0) setPoProjectId(ALL_PROJECTS_VALUE);
      else if (primaryProjectId) setPoProjectId(primaryProjectId);
    }
  }, [invoiceProjectId, poProjectId, primaryProjectId, projectIds.length]);

  const handleCreateInvoice = () => {
    if (!invoiceProjectId || invoiceProjectId === ALL_PROJECTS_VALUE) return;
    navigate(`/projects/${encodeURIComponent(invoiceProjectId)}/invoices/new`);
  };

  const handleReviewInvoices = () => {
    if (!invoiceProjectId || invoiceProjectId === ALL_PROJECTS_VALUE) return;
    navigate(`/projects/${encodeURIComponent(invoiceProjectId)}/invoices`);
  };

  const handleIssuePO = () => {
    if (!poProjectId || poProjectId === ALL_PROJECTS_VALUE) return;
    navigate(`/projects/${encodeURIComponent(poProjectId)}/purchase-orders/new`);
  };

  const handleReviewPOs = () => {
    if (!poProjectId || poProjectId === ALL_PROJECTS_VALUE) return;
    navigate(`/projects/${encodeURIComponent(poProjectId)}/purchase-orders`);
  };

  const scrollProjects = (direction) => {
    if (!sliderRef.current) return;
    sliderRef.current.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  const getProjectCover = (project) => {
    const raw = project?.raw || project;
    return (
      raw?.coverImageUrl ||
      raw?.coverImage ||
      raw?.imageUrl ||
      raw?.image ||
      raw?.thumbnailUrl ||
      ''
    );
  };

  if (loading)
    return (
      <div className="dashboard-root content-wrap">
        <h1 className="page-title">Dashboard</h1>
        <p>Loading data...</p>
      </div>
    );

  /* ------------------ RENDER ------------------ */
  return (
    <div className="dashboard-root content-wrap">
      <h1 className="page-title">Dashboard</h1>

      {/* Recent Projects */}
      <div className="card recent-projects-card">
        <div className="recent-projects-header">
          <div>
            <h4>Recent Projects</h4>
            <span className="muted">Updated recently</span>
          </div>
          <div className="recent-projects-controls">
            <button
              className="slider-btn"
              onClick={() => scrollProjects(-1)}
              type="button"
              aria-label="Scroll recent projects left"
            >
              ‹
            </button>
            <button
              className="slider-btn"
              onClick={() => scrollProjects(1)}
              type="button"
              aria-label="Scroll recent projects right"
            >
              ›
            </button>
          </div>
        </div>
        <div className="recent-projects-slider" ref={sliderRef}>
          {recentProjectCards.length === 0 ? (
            <div className="recent-projects-empty">No projects to show yet.</div>
          ) : (
            recentProjectCards.map((project) => {
              const cover = getProjectCover(project);
              const projId = project.id || project.projectId;
              return (
                <Link
                  to={`/projects/${encodeURIComponent(projId)}`}
                  className="project-slide"
                  key={projId}
                >
                  <div
                    className={`project-cover ${cover ? '' : 'no-cover'}`}
                    style={cover ? { backgroundImage: `url(${cover})` } : undefined}
                  >
                    <div className="project-overlay">
                      <div className="project-name">{project.name || 'Untitled'}</div>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="project-summary-row">
        <div className="summary-card">
          <div className="summary-title">Total Invoiced</div>
          <div className="summary-value">{formatCurrency(totalInvoices)}</div>
          <div className="summary-sub">Inception to date</div>
        </div>
        <div className="summary-card">
          <div className="summary-title">Payments Received</div>
          <div className="summary-value">{formatCurrency(invoiceTotals.paid || 0)}</div>
          <div className="summary-sub">Inception to date</div>
        </div>
        <div className="summary-card">
          <div className="summary-title">Open Invoice Balance</div>
          <div className="summary-value">{formatCurrency(openBalance)}</div>
          <div className="summary-sub">Current</div>
        </div>
        <div className="summary-card">
          <div className="summary-title">Total Purchase Orders</div>
          <div className="summary-value">{formatCurrency(totalPOs)}</div>
          <div className="summary-sub">Inception to date</div>
        </div>
        <div className="summary-card">
          <div className="summary-title">Payments Made</div>
          <div className="summary-value">{formatCurrency(poTotals.paid || 0)}</div>
          <div className="summary-sub">Inception to date</div>
        </div>
        <div className="summary-card">
          <div className="summary-title">Open Purchase Order Balance</div>
          <div className="summary-value">{formatCurrency(poTotals.open || 0)}</div>
          <div className="summary-sub">Current</div>
        </div>
      </div>

      {/* Charts + Agenda */}
      <div className="dashboard-grid">
        {/* Invoices */}
        <div className="card accounts-card">
          <div className="card-header">
            <div>
              <h4>Invoices</h4>
              <span className="muted">Inception to date</span>
            </div>
          </div>
          <div className="card-subrow">
            <label className="project-picker">
              <span>Project</span>
              <select
                value={invoiceProjectId || ''}
                onChange={(e) => setInvoiceProjectId(e.target.value)}
              >
                <option value="" disabled>
                  Select a project
                </option>
                {projects.length > 0 && <option value={ALL_PROJECTS_VALUE}>All Projects</option>}
                {projects.map((p) => {
                  const pid = p.projectId || p.id;
                  const label =
                    p.projectNickname || p.projectName || p.name || p.title || 'Untitled';
                  return (
                    <option key={pid} value={pid}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
          <div className="invoices-metrics">
            <div className="invoice-metric">
              <div className="metric-label">Total Invoices</div>
              <div className="metric-value">{selectedInvoiceCounts.total}</div>
            </div>
            <div className="invoice-metric">
              <div className="metric-label">Unpaid Invoices</div>
              <div className="metric-value">{selectedUnpaidInvoiceCount}</div>
            </div>
            <div className="invoice-metric">
              <div className="metric-label">Open Balance</div>
              <div className="metric-value">{formatCurrency(selectedOpenBalance)}</div>
            </div>
          </div>
          <div className="invoices-actions">
            {!isAllInvoicesSelected && (
              invoiceProjectId ? (
                <Link
                  className="dashboard-btn primary"
                  to={`/projects/${encodeURIComponent(invoiceProjectId)}/invoices/new`}
                >
                  Create New Invoice
                </Link>
              ) : (
                <button
                  className="dashboard-btn primary"
                  disabled
                  type="button"
                >
                  Create New Invoice
                </button>
              )
            )}
            <button
              className="dashboard-btn subtle"
              onClick={handleReviewInvoices}
              disabled={
                !invoiceProjectId || isAllInvoicesSelected || selectedUnpaidInvoiceCount === 0
              }
              type="button"
            >
              Send reminders for {selectedUnpaidInvoiceCount} unpaid invoices
            </button>
          </div>
          <div className="card-body center-chart">
            <div className="chart-box">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 8, right: 8, bottom: 28, left: 8 }}>
                  <Pie
                    data={invoiceChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={92}
                    label={false}
                  >
                    {invoiceChartData.map((entry, i) => (
                      <Cell key={i} fill={CHART_COLORS[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="chart-legend" aria-label="Invoice chart legend">
              {invoiceChartData.map((entry) => (
                <li key={entry.name}>
                  <span
                    className="legend-swatch"
                    style={{ background: CHART_COLORS[entry.name] }}
                  />
                  <span>{entry.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Purchase Orders */}
        <div className="card procurement-card">
          <div className="card-header">
            <div>
              <h4>Purchase Orders</h4>
              <span className="muted">Open balance</span>
            </div>
          </div>
          <div className="card-subrow">
            <label className="project-picker">
              <span>Project</span>
              <select value={poProjectId || ''} onChange={(e) => setPoProjectId(e.target.value)}>
                <option value="" disabled>
                  Select a project
                </option>
                {projects.length > 0 && <option value={ALL_PROJECTS_VALUE}>All Projects</option>}
                {projects.map((p) => {
                  const pid = p.projectId || p.id;
                  const label =
                    p.projectNickname || p.projectName || p.name || p.title || 'Untitled';
                  return (
                    <option key={pid} value={pid}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
          <div className="procurement-metrics">
            <div className="procurement-metric">
              <div className="metric-label">Total POs</div>
              <div className="metric-value">{selectedPoCounts.total}</div>
            </div>
            <div className="procurement-metric">
              <div className="metric-label">Outstanding POs</div>
              <div className="metric-value">{selectedOutstandingPoCount}</div>
            </div>
            <div className="procurement-metric">
              <div className="metric-label">Open Balance</div>
              <div className="metric-value">{formatCurrency(selectedPoTotals.open || 0)}</div>
            </div>
          </div>
          <div className="procurement-actions">
            {!isAllPOsSelected && (
              poProjectId ? (
                <Link
                  className="dashboard-btn primary"
                  to={`/projects/${encodeURIComponent(poProjectId)}/purchase-orders/new`}
                >
                  Create New PO
                </Link>
              ) : (
                <button
                  className="dashboard-btn primary"
                  disabled
                  type="button"
                >
                  Create New PO
                </button>
              )
            )}
            <button
              className="dashboard-btn subtle"
              onClick={handleReviewPOs}
              disabled={!poProjectId || isAllPOsSelected || selectedOutstandingPoCount === 0}
              type="button"
            >
              Review Outstanding POs
            </button>
          </div>
          <div className="card-body center-chart">
            <div className="chart-box">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 8, right: 8, bottom: 28, left: 8 }}>
                  <Pie
                    data={poChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={92}
                    label={false}
                  >
                    {poChartData.map((entry, i) => (
                      <Cell key={i} fill={CHART_COLORS[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="chart-legend" aria-label="Purchase order chart legend">
              {poChartData.map((entry) => (
                <li key={entry.name}>
                  <span
                    className="legend-swatch"
                    style={{ background: CHART_COLORS[entry.name] }}
                  />
                  <span>{entry.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Agenda */}
        <div className="card agenda-card">
          <div className="agenda-header">
            <h4 className="card-heading">Today's Agenda</h4>
            <div className="agenda-controls">
              <label className="agenda-sort">
                <span>Filter</span>
                <select value={agendaFilter} onChange={(e) => setAgendaFilter(e.target.value)}>
                  <option value="all">All team</option>
                  <option value="unassigned">Unassigned</option>
                  {agendaTeamOptions.map((member) => (
                    <option key={member.email} value={member.email}>
                      {member.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="agenda-sort">
                <span>Sort</span>
                <select value={agendaSort} onChange={(e) => setAgendaSort(e.target.value)}>
                  <option value="assignee-asc">Team member (A-Z)</option>
                  <option value="assignee-desc">Team member (Z-A)</option>
                  <option value="default">Default</option>
                </select>
              </label>
            </div>
          </div>
          <div className="agenda-table">
            {todayAgenda.length === 0 ? (
              <div className="agenda-empty">
                <div className="agenda-empty-title">No Tasks Due Today</div>
                <div className="agenda-empty-sub">You're fully up to date.</div>
              </div>
            ) : (
              <table className="agenda-table-inner">
                <thead>
                  <tr>
                    <th style={{ width: '32px' }} />
                    <th>Task</th>
                    <th>Project</th>
                    <th>Assigned</th>
                    <th style={{ textAlign: 'right' }}>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTodayAgenda.map((a) => (
                    <tr key={a.todoId}>
                      <td>
                        <input type="checkbox" checked={a.completed} readOnly />
                      </td>
                      <td className="agenda-task">{a.title}</td>
                      <td className="agenda-project">{a.projectName || '-'}</td>
                      <td className="agenda-assignees">
                        {Array.isArray(a.assignees) && a.assignees.length > 0
                          ? a.assignees.map((id) => assigneeName(id)).join(', ')
                          : '-'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{a.dueDate?.split('T')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
